import React, { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { REFRESH_PIXEL_STATUS } from '@/graphql/media-buying-queries'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Badge } from '@/components/ui/badge'
import { useWizard } from '@/components/mediaBuying/adCampaign/AdCampaignWizard'
import { AwarenessIcon, TrafficIcon, EngagementIcon, LeadsIcon, SalesIcon, ExternalLinkIcon } from '@/components/icons'
import { useParams } from 'react-router-dom'
import { useMediaBuyingContext } from '@/context/media-buying-context.jsx'
import { filterAudiencesByObjective, getAvailableStages } from '@/utils/ad-campaigns-helpers'
import Funnel from '@/components/funnel'
import { Tour } from '@/components/Tour'
import InlineInfo from '../InlineInfo'
import SignalPanel from '../SignalPanel'
import AudienceReadinessPanel from '../AudienceReadinessPanel'

const CAMPAIGN_OBJECTIVES = [
    {
        id: 'awareness',
        icon: AwarenessIcon,
        title: 'Awareness',
        description: 'Show your ads to people who are most likely to remember them. Good for:',
        badges: ['Reach', 'Brand awareness', 'Video views', 'Store location awareness'],
    },
    {
        id: 'traffic',
        icon: TrafficIcon,
        title: 'Traffic',
        description: 'Send people to a destination, such as your website, app, Instagram profile or Facebook event.',
        linkText: 'About traffic',
        linkUrl: 'https://www.facebook.com/business/help/301780226847564?id=1646770532043319',
        linkDescription: 'Good for:',
        badges: [
            'Link clicks',
            'Landing page views',
            'Instagram profile visits',
            'Messenger, Instagram and WhatsApp',
            'Calls',
        ],
    },
    {
        id: 'engagement',
        icon: EngagementIcon,
        title: 'Engagement',
        description:
            'Get more messages, purchases through messaging, video views, post engagement, Page likes or event responses. Good for:',
        badges: ['Messenger, Instagram and WhatsApp', 'Video views', 'Post engagement', 'Conversions', 'Calls'],
    },
    {
        id: 'leads',
        icon: LeadsIcon,
        title: 'Leads',
        description: 'Collects leads for your business or brand. Good for:',
        badges: ['Website and instant forms', 'Instant forms', 'Messenger and Instagram', 'Conversions', 'Calls'],
    },
    // {
    //     id: 'app_promotion',
    //     icon: AppPromotionIcon,
    //     title: 'App promotion',
    //     description: 'Find new people to install your app and continue using it.',
    //     linkText: 'About app promotion',
    //     linkUrl: 'https://www.facebook.com/business/help/606803340313430',
    //     linkDescription: 'Good for:',
    //     badges: ['App installs', 'App events'],
    // },
    {
        id: 'sales',
        icon: SalesIcon,
        title: 'Sales',
        description: 'Find people who are likely to purchase your product or service. Good for:',
        badges: ['Conversions', 'Catalogue sales', 'Messenger, Instagram and WhatsApp', 'Calls'],
    },
]

const TourFunneStep = () => {
    return (
        <div className="tw:text-sm">
            Only Acquisition sales funnel stage is available for Awareness campaign objective.
            <div className="tw:flex tw:flex-col tw:gap-1 tw:mt-4">
                <span className="tw:text-sm tw:font-bold">Learn more</span>
                <a
                    href="https://www.facebook.com/business/ads/ad-objectives"
                    target="_blank"
                    className="tw:text-blue-300 tw:text-sm tw:font-semibold tw:flex tw:gap-1 tw:items-center"
                >
                    <ExternalLinkIcon /> Ad objectives
                </a>
                <a
                    href="https://www.facebook.com/business/ads/ad-objectives/awareness"
                    target="_blank"
                    className="tw:text-blue-300 tw:text-sm tw:font-semibold tw:flex tw:gap-1 tw:items-start"
                >
                    <ExternalLinkIcon className="tw:min-h-6 tw:min-w-6" /> Expand your reach and maintain a strong
                    presence with customers using the awareness objective.
                </a>
            </div>
        </div>
    )
}

const CampaignObjective = () => {
    const { getStepContentData, setStepContentData, registerNextGuard, clearWizardStorage, showGuidePopups } =
        useWizard()
    const { connectedPublishers } = useMediaBuyingContext()
    const publishedAt = Object.values(connectedPublishers)?.[0]
    const params = useParams()

    // Initialize state from saved data or default to 'awareness'
    const [selectedObjective, setSelectedObjective] = useState(() => {
        const savedData = getStepContentData()
        return savedData?.selectedObjective || 'awareness'
    })

    // Get pixel status from connected publishers
    const pixelIsValid = publishedAt?.pixel_active

    const [refreshPixelStatus] = useMutation(REFRESH_PIXEL_STATUS, {
        onCompleted: (data) => {
            // Update the wizard state with the refreshed pixel status
            setStepContentData({ selectedObjective, pixelIsValid: data?.refreshPixelStatus })
        },
        onError: (error) => {
            console.error('Failed to refresh pixel status:', error)
        },
    })

    // Save initial data and whenever selection changes
    useEffect(() => {
        setStepContentData({ selectedObjective, pixelIsValid })
    }, [selectedObjective, pixelIsValid, setStepContentData])

    // Check and refresh pixel status if it's false
    useEffect(() => {
        if (pixelIsValid === false && publishedAt?.publisher_id && params?.id) {
            refreshPixelStatus({
                variables: {
                    publisher_id: publishedAt.publisher_id,
                    campaign_id: params.id,
                },
            })
        }
    }, [pixelIsValid, publishedAt?.publisher_id, params?.id, refreshPixelStatus])

    // Register next guard to ensure selection is made
    useEffect(() => {
        const nextGuard = () => {
            const hasObjective = selectedObjective !== null

            if (hasObjective) {
                // Get current salesFunnel data
                const salesFunnelData = getStepContentData('salesFunnelSetup') || {}
                const currentAudienceSelections = salesFunnelData.audienceSelections || {}

                // Filter audiences based on objective
                const filteredAudienceSelections = filterAudiencesByObjective(
                    currentAudienceSelections,
                    selectedObjective,
                )

                // Update salesFunnelSetup with filtered audiences
                setStepContentData(
                    {
                        ...salesFunnelData,
                        audienceSelections: filteredAudienceSelections,
                    },
                    'salesFunnelSetup',
                )
            }

            return hasObjective
        }
        registerNextGuard(nextGuard)
    }, [selectedObjective, registerNextGuard])

    const handleObjectiveChange = (objectiveId) => {
        clearWizardStorage()
        setSelectedObjective(objectiveId)

        // Refresh pixel status when objective changes if pixel is inactive
        if (pixelIsValid === false && publishedAt?.publisher_id && params?.id) {
            refreshPixelStatus({
                variables: {
                    publisher_id: publishedAt.publisher_id,
                    campaign_id: params.id,
                },
            })
        }
    }

    // Get available stages for the selected objective (default to 'awareness' for display)
    const availableStages = getAvailableStages(selectedObjective || 'awareness')

    const showFunnelTour = () => {
        if (selectedObjective !== 'awareness') return false
        return showGuidePopups
    }

    const steps = [
        {
            target: '.funnel-tour',
            content: <TourFunneStep />,
            placement: 'left',
            disableBeacon: true,
        },
    ]

    const showAwarnessInlineInfo = (objective) => {
        return objective.id === 'awareness' && !showGuidePopups
    }

    return (
        <>
            <Tour steps={steps} run={showFunnelTour()} />
            <div className="tw:flex tw:w-full tw:h-full tw:min-h-0">
                <div className="tw:flex-1 tw:pb-6 tw:overflow-y-auto">
                    <div className="tw:p-6 tw:max-w-[580px]">
                        <RadioGroup
                            value={selectedObjective}
                            onValueChange={handleObjectiveChange}
                            className="tw:space-y-4 tw:gap-0"
                        >
                            {CAMPAIGN_OBJECTIVES.map((objective) => {
                                const IconComponent = objective.icon
                                return (
                                    <React.Fragment key={objective.id}>
                                        <div
                                            className="tw:flex tw:items-start tw:gap-4 tw:rounded-lg tw:border tw:border-transparent hover:tw:border-gray-600 tw:transition-colors"
                                        >
                                            <div className="tw:flex tw:items-center tw:gap-3">
                                                <RadioGroupItem value={objective.id} id={objective.id} />
                                                <IconComponent className="tw:flex-shrink-0 tw:w-6 tw:h-6" />
                                            </div>
                                            <div className="tw:flex-1 tw:min-w-0">
                                                <label htmlFor={objective.id} className="tw:cursor-pointer tw:block">
                                                    <h3 className="tw:!text-sm tw:font-semibold tw:text-white tw:mb-2">
                                                        {objective.title}
                                                    </h3>
                                                    <p className="tw:text-sm tw:font-normal tw:mb-3">
                                                        {objective.description}
                                                        {objective.linkText && (
                                                            <>
                                                                {' '}
                                                                <a
                                                                    href={objective.linkUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="tw:text-[#3871FF] tw:font-semibold tw:cursor-pointer tw:underline"
                                                                >
                                                                    {objective.linkText}
                                                                </a>
                                                                {objective.linkDescription && (
                                                                    <span className="tw:font-normal">
                                                                        . {objective.linkDescription}
                                                                    </span>
                                                                )}
                                                            </>
                                                        )}
                                                    </p>
                                                    <div className="tw:flex tw:flex-wrap tw:gap-2">
                                                        {objective.badges.map((badge, index) => (
                                                            <Badge
                                                                key={index}
                                                                variant="campaign"
                                                                className="tw:text-sm tw:text-[#fff] tw:px-2.5 tw:py-0.5 tw:rounded-full"
                                                            >
                                                                {badge}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                        {showAwarnessInlineInfo(objective) && (
                                            <InlineInfo text="Only Acquisition sales funnel stage is available for Awareness campaign objective." />
                                        )}
                                    </React.Fragment>
                                )
                            })}
                        </RadioGroup>

                        <div className="tw:pt-6">
                            <a
                                href="https://www.facebook.com/business/help/325793898950394"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tw:text-[#3871FF] tw:text-sm tw:font-semibold tw:cursor-pointer tw:underline"
                            >
                                About campaign objectives
                            </a>
                        </div>

                        <SignalPanel
                            publisherId={publishedAt?.publisher_id}
                            campaignId={params?.id}
                            objective={selectedObjective}
                        />
                        <AudienceReadinessPanel
                            publisherId={publishedAt?.publisher_id}
                            campaignId={params?.id}
                            objective={selectedObjective}
                        />
                    </div>
                </div>
                <div className="tw:border-l tw:w-fit tw:flex-none tw:ml-auto tw:h-full">
                    <div className="funnel-tour tw:px-10 tw:py-6">
                        <Funnel availableStages={availableStages} audienceSelections={{}} />
                    </div>
                </div>
            </div>
        </>
    )
}

export default CampaignObjective
